import type { BasketItem, Product } from '../types/order';
import { feeUnitGross, isFeeProduct } from './pricing';

/**
 * Αυτόματη γραμμή του τέλους πλαστικού (κωδ. 16) στο καλάθι TakeAway/Delivery.
 *
 * Ζούσε αντιγραμμένο μέσα σε δύο useEffect (TakeAway, DeliveryWithMap). Το ποσό είναι ΕΝΑ, από
 * το state_fees (απόφαση 1_fees_source, feeUnitGross του pricing.ts), και το τέλος μπαίνει ΠΑΝΤΑ
 * χωρίς μέγεθος. Καθαρή συνάρτηση για να δοκιμάζεται χωρίς οθόνη.
 *
 * ΣΥΜΒΟΛΑΙΟ: επιστρέφει το ΙΔΙΟ αντικείμενο basket όταν δεν αλλάζει τίποτα. Ο καλών κάνει
 * `if (next !== basket) setBasket(next)` μέσα σε effect που εξαρτάται από το basket· ένα νέο
 * αντικείμενο σε κάθε κλήση θα ξανάτρεχε το effect επ' άπειρον.
 *
 * Κανόνες ποσότητας ΙΔΙΟΙ με πριν:
 *  - απαιτούμενα = Σ (ποσότητα × auto_plastic_fee_count) στις γραμμές που ΔΕΝ είναι τέλη·
 *  - feeOverride (ο ταμίας χαρίζει/διορθώνει) υπερισχύει·
 *  - «υπάρχουσα» γραμμή = η γραμμή του αυτοματισμού (managedFeeIndex): ΠΡΩΤΑ η δική μας
 *    (`auto-fee-16-…`)· χωρίς αυτήν, και ΜΟΝΟ χωρίς επιλογή του ταμία (feeOverride null), η
 *    πρώτη γραμμή του ίδιου προϊόντος-τέλους 16 (και η χειροκίνητη ή η ενωμένη της επανάληψης),
 *    όπως πριν· σε αυτήν αλλάζει ΜΟΝΟ η ποσότητα (το ποσό της μένει όπως μπήκε). Μια χειροκίνητη
 *    γραμμή ΔΙΠΛΑ στη δική μας δεν αγγίζεται (19/10/2026: «Ναι, Take Away και Delivery» — η
 *    χειροκίνητη γραμμή τέλους συμπεριφέρεται όπως κάθε είδος: ×, ±, ένωση)·
 *  - γραμμές με keepHistoricalFee (επανάληψη παραγγελίας, αποφάσεις F1-W6-2/F1-W6-3) ούτε
 *    μετράνε ούτε είναι «υπάρχουσα» γραμμή: η επανάληψη κρατά ΑΚΡΙΒΩΣ τα ιστορικά τέλη και ο
 *    κανόνας πιάνει μόνο ό,τι προσθέτει/αλλάζει ο ταμίας μετά (utils/reorderLine)·
 *  - autoEnabled === false (διακόπτης auto_plastic_fee_enabled, μόνο στο TakeAway): φεύγει
 *    ΜΟΝΟ η γραμμή που βάλαμε εμείς (id `auto-fee-16-…`)· η χειροκίνητη μένει.
 */

/** Πρόθεμα του id της γραμμής που βάζει ο αυτοματισμός — την ξεχωρίζει από τη χειροκίνητη. */
export const AUTO_FEE_ID_PREFIX = 'auto-fee-16-';

export interface AutoFeeInput {
  basket: BasketItem[];
  /** products.find(fee_category === 16) της λίστας POS· undefined = το κατάστημα δεν έχει τέλος. */
  feeProduct: Product | null | undefined;
  /** null = ακολουθεί τον αυτόματο υπολογισμό· αριθμός = επιλογή του ταμία. */
  feeOverride: number | null;
  /** Μόνο το ρητό false κλείνει τον αυτοματισμό (ρυθμίσεις που δεν φόρτωσαν = ενεργό). */
  autoEnabled?: boolean;
  /** Για το id της νέας γραμμής· στα tests σταθερό. */
  now?: number;
}

/**
 * Πόσα τέλη 16 ζητά ο αυτόματος κανόνας: Σ (ποσότητα × auto_plastic_fee_count) στις γραμμές
 * που ΔΕΝ είναι τέλη.
 *
 * ΕΚΤΟΣ από τις γραμμές με keepHistoricalFee (επανάληψη παραγγελίας, F1-W6-2/F1-W6-3): το τέλος
 * τους είναι ήδη μέσα στην ιστορική τιμή (πλατφόρμα), στην ιστορική γραμμή τέλους, ή χαρίστηκε.
 */
export function autoFeeRequiredQty(basket: BasketItem[]): number {
  let required = 0;
  for (const item of basket) {
    if (item.product.fee_category) continue; // οι γραμμές τελών δεν γεννούν τέλος
    if (item.keepHistoricalFee) continue; // επανάληψη: τα ιστορικά τέλη μένουν ακριβώς (F1-W6-2/3)
    const triggerCount = item.product.auto_plastic_fee_count || 0;
    if (triggerCount > 0) required += item.quantity * triggerCount;
  }
  return required;
}

/** Η γραμμή την έβαλε ο αυτοματισμός (πρόθεμα του id) — όχι ο ταμίας. */
export function isOwnAutoFeeLine(item: Pick<BasketItem, 'id'>): boolean {
  return String(item.id).startsWith(AUTO_FEE_ID_PREFIX);
}

function withoutAutoLine(basket: BasketItem[]): BasketItem[] {
  const autoIdx = basket.findIndex(isOwnAutoFeeLine);
  return autoIdx >= 0 ? basket.filter((_, idx) => idx !== autoIdx) : basket;
}

/**
 * Η θέση της γραμμής που ενημερώνει ο αυτοματισμός (−1 = καμία· τότε μπαίνει νέα δική μας).
 *
 *  1. Η δική μας γραμμή (`auto-fee-16-…`), όταν υπάρχει: μια χειροκίνητη γραμμή δίπλα της μένει
 *     του ταμία, ακόμη κι αν προστέθηκε πρώτη.
 *  2. Αλλιώς, ΜΟΝΟ όσο ο ταμίας δεν έχει δική του επιλογή (feeOverride null), η πρώτη γραμμή του
 *     ίδιου προϊόντος-τέλους 16 — χειροκίνητη ή η ενωμένη της επανάληψης (followRuleAfterCashierChange),
 *     όπως πριν. Έτσι μένει ο κανόνας «αυτόματο ενεργό, κανένα είδος που γεννά τέλος → φεύγει και
 *     η χειροκίνητη γραμμή 16» (επιβεβαιωμένος από τον ιδιοκτήτη).
 *     Με επιλογή του ταμία (× ή ± στη γραμμή του αυτοματισμού) ΔΕΝ υιοθετείται χειροκίνητη:
 *     αλλιώς το × στη δική μας γραμμή θα έσβηνε στον επόμενο γύρο και τη χειροκίνητη, και ένα
 *     τέλος που ο ταμίας προσθέτει μετά το «χάρισμα» θα εξαφανιζόταν αμέσως.
 * Ποτέ η ιστορική γραμμή της επανάληψης (keepHistoricalFee, F1-W6-2/F1-W6-3).
 */
function managedFeeIndex(basket: BasketItem[], feeProduct: Product, feeOverride: number | null): number {
  const candidate = (b: BasketItem) =>
    b.product.fee_category === 16 && b.product.id === feeProduct.id && !b.keepHistoricalFee;
  const own = basket.findIndex(b => candidate(b) && isOwnAutoFeeLine(b));
  if (own >= 0) return own;
  if (feeOverride !== null) return -1;
  return basket.findIndex(candidate);
}

/**
 * Το id της γραμμής τέλους που διαχειρίζεται ο αυτοματισμός στο καλάθι — ΑΥΤΗ και μόνο αυτή
 * περνά στην οθόνη ως «αυτόματη» (× / ± = feeOverride). Ό,τι άλλο είναι γραμμή του ταμία: ×
 * αφαιρεί, ± αλλάζει ποσότητα, νέο πάτημα ενώνεται (addOrMergeManualFee).
 * null όταν ο αυτοματισμός δεν τρέχει: κλειστός διακόπτης, χωρίς προϊόν-τέλος, χωρίς ποσό
 * state_fees (απόφαση 33) ή όταν δεν υπάρχει τέτοια γραμμή. Ίδιος κανόνας με το computeAutoFeeBasket.
 */
export function autoManagedFeeLineId({ basket, feeProduct, feeOverride, autoEnabled }: Omit<AutoFeeInput, 'now'>): string | null {
  if (!feeProduct || autoEnabled === false) return null;
  if (feeUnitGross(feeProduct) === null) return null;
  const idx = managedFeeIndex(basket, feeProduct, feeOverride);
  return idx >= 0 ? basket[idx].id : null;
}

export function computeAutoFeeBasket({ basket, feeProduct, feeOverride, autoEnabled, now }: AutoFeeInput): BasketItem[] {
  if (!feeProduct) return basket;

  if (autoEnabled === false) return withoutAutoLine(basket);

  // Απόφαση 33_fee_zero: χωρίς ποσό state_fees ΔΕΝ μπαίνει γραμμή (ούτε 0 €)· ο server κάνει
  // log + συναγερμό και η έκδοση συνεχίζει χωρίς τέλος. Φεύγει και μια αυτόματη γραμμή που
  // μπήκε πριν χαθεί το ποσό, για να μην τυπωθεί ποσό που δεν ισχύει. Το μήνυμα στην κονσόλα
  // το γράφει ήδη το pricing.feeUnitGross (μία φορά ανά προϊόν, και για πεδίο που λείπει).
  const unit = feeUnitGross(feeProduct);
  if (unit === null) return withoutAutoLine(basket);

  const required = autoFeeRequiredQty(basket);
  const target = feeOverride !== null ? feeOverride : required;

  // Η ιστορική γραμμή τέλους της επανάληψης (keepHistoricalFee) ΔΕΝ είναι «υπάρχουσα»: μένει
  // όπως ήρθε και τα νέα είδη παίρνουν δική τους γραμμή στο τρέχον ποσό (F1-W6-2/F1-W6-3).
  // Πρώτα η δική μας γραμμή· η χειροκίνητη μόνο χωρίς επιλογή του ταμία (managedFeeIndex).
  const existingIdx = managedFeeIndex(basket, feeProduct, feeOverride);
  const currentQty = existingIdx >= 0 ? basket[existingIdx].quantity : 0;
  if (target === currentQty) return basket;

  if (target <= 0) {
    return existingIdx >= 0 ? basket.filter((_, idx) => idx !== existingIdx) : basket;
  }
  if (existingIdx >= 0) {
    return basket.map((it, idx) => idx === existingIdx ? { ...it, quantity: target } : it);
  }
  return [
    ...basket,
    {
      id: `${AUTO_FEE_ID_PREFIX}${feeProduct.id}-${now ?? Date.now()}`,
      product: feeProduct,
      selectedSize: null,
      quantity: target,
      removedIngredients: [],
      addedExtras: [],
      specialInstructions: '',
      totalPrice: unit,
      price: unit
    }
  ];
}

/**
 * Γραμμή τέλους (fee_category 8/16) που προσθέτει ο ταμίας με το χέρι — click, long-press ή
 * barcode, TakeAway και Delivery.
 *
 * Ζει εδώ και όχι μέσα στις οθόνες για να δοκιμάζεται: η ΜΟΝΗ αλλαγή ποσού του FE-2 (7700
 * «ΣΑΚΟΥΛΑ» με το μέγεθος 13932: 0,18 → 0,09, απόφαση 1_fees_source) γίνεται σε αυτό το σημείο,
 * και μια σύγκρουση merge στα handleProductClick/LongPress θα την έχανε σιωπηλά αν η λογική
 * έμενε αντιγραμμένη στις δύο οθόνες.
 *
 * - ποσό = feeUnitGross (state_fees)· ΠΑΝΤΑ selectedSize null — ούτε μέγεθος, ούτε option groups,
 *   ούτε ανοιχτή τιμή/ζύγι· αλλιώς βάση + μέγεθος = διπλή χρέωση·
 * - χωρίς ποσό → null (απόφαση 33_fee_zero): ο καλών δείχνει μήνυμα και ΔΕΝ βάζει γραμμή·
 * - μη τέλος → null (ο καλών δεν πρέπει να φτάσει εδώ με κανονικό προϊόν).
 * Το τέλος δεν έχει τιμή ανά κανάλι, γι' αυτό η συνάρτηση δεν παίρνει κανάλι.
 */
export function manualFeeLine(product: Product, now?: number): BasketItem | null {
  if (!isFeeProduct(product)) return null;
  const unit = feeUnitGross(product);
  if (unit === null) return null;
  return {
    id: `${product.id}-${now ?? Date.now()}`,
    product,
    selectedSize: null,
    quantity: 1,
    removedIngredients: [],
    addedExtras: [],
    specialInstructions: '',
    discount: undefined,
    totalPrice: unit,
    price: unit
  };
}

const feeCents = (value: unknown): number => {
  const n = typeof value === 'number' ? value : parseFloat(String(value));
  return Number.isFinite(n) ? Math.round(n * 100) : NaN;
};

/**
 * Το καλάθι μετά από χειροκίνητο τέλος (click / long-press / barcode): δεύτερο πάτημα στο ίδιο
 * τέλος ανεβάζει την ποσότητα της ΧΕΙΡΟΚΙΝΗΤΗΣ γραμμής του, όπως κάθε είδος (utils/basketMerge,
 * που τα τέλη τα εξαιρεί λόγω των κανόνων ποσότητας του αυτοματισμού). 19/10/2026: «Ναι, Take Away
 * και Delivery».
 *
 * Ενώνεται ΜΟΝΟ με γραμμή του ίδιου προϊόντος-τέλους, ίδιο ποσό μονάδας (σε λεπτά — ποτέ αλλαγή
 * ποσού), χωρίς έκπτωση, που ΔΕΝ είναι:
 *  - η γραμμή του αυτοματισμού (`managedId`, από το autoManagedFeeLineId) ή γραμμή με πρόθεμα
 *    `auto-fee-16-`: την ποσότητά της την ορίζει ο κανόνας/feeOverride, και +1 εκεί θα
 *    ξαναγύριζε στον επόμενο γύρο του effect·
 *  - γραμμή επανάληψης (ιστορικό ποσό, F1-W6-2/F1-W6-3).
 * Αλλιώς νέα γραμμή στο τέλος, όπως πριν.
 */
export function addOrMergeManualFee(basket: BasketItem[], line: BasketItem, managedId: string | null): BasketItem[] {
  const unit = feeCents(line.price ?? line.totalPrice);
  const target = Number.isFinite(unit) ? basket.find(b =>
    b.id !== managedId &&
    !isOwnAutoFeeLine(b) &&
    b.product.fee_category != null &&
    Number(b.product.id) === Number(line.product.id) &&
    !b.keepHistoricalFee &&
    !b.fromReorder &&
    !b.weight &&
    !b.applied_promotion_id &&
    !(Number(b.discount) > 0) &&
    feeCents(b.price ?? b.totalPrice) === unit
  ) : undefined;
  if (!target) return [...basket, line];
  return basket.map(b => (b.id === target.id ? { ...b, quantity: b.quantity + line.quantity } : b));
}

/**
 * Ποσό τέλους ανά τεμάχιο για τη σημείωση «+ τέλος €X / τεμ.» κάτω από τα προϊόντα που το
 * γεννούν. Ίδια πηγή με τη γραμμή (feeUnitGross)· χωρίς ποσό ή χωρίς τέλος → 0 (καμία σημείωση).
 */
export function autoFeeNoteUnit(feeProduct: Product | null | undefined): number {
  return feeProduct ? feeUnitGross(feeProduct) ?? 0 : 0;
}
